import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

let moneyFormattingPromise;
const loadMoneyFormatting = () => (moneyFormattingPromise ??= import('@theme/money-formatting'));

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const COLLECTION_RESULTS_SELECTOR = '#ResultsList';
const COLLECTION_RESULTS_COUNT_SELECTOR = '[data-kv-results-count]';
const COLLECTION_FILTER_FADE_MS = 180;

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  #collectionRenderVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('submit', this.#onSubmit);
    // In vertical filter mode the in-page filters take over at ≥750px, so a
    // drawer left open across a mobile→desktop resize would render duplicate
    // filter UI. `data-filter-style` is only set on the drawer variant (see
    // blocks/filters.liquid), so matching "vertical" here uniquely targets it.
    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#onSubmit);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #onSubmit = (event) => {
    if (
      event.defaultPrevented ||
      event.target !== this.refs.facetsForm ||
      !this.#isMobileStagedSession() ||
      !this.closest('collection-component.kervelan-collection')
    ) return;

    event.preventDefault();
    this.commitStagedFilters()?.catch(() => window.location.replace(window.location.href));

    const drawer = this.closest('theme-drawer');
    if (drawer instanceof HTMLElement && typeof drawer.close === 'function') drawer.close();
  };

  #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) return;
    const drawer = document.getElementById('filters-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  };

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    if (this.closest('collection-component.kervelan-collection')) {
      const sortBy = newParameters.get('sort_by') || new URL(window.location.href).searchParams.get('sort_by');
      if (sortBy) newParameters.set('sort_by', sortBy);
    }

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Pushes the current filter parameters into browser history.
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    const state = { urlParameters: urlParameters.toString() };
    if (this.closest('collection-component.kervelan-collection')) {
      history.replaceState(state, '', url.toString());
    } else {
      history.pushState(state, '', url.toString());
    }
    this.#syncCollectionFormsFromURL();
  }

  /**
   * Whether this drawer is intentionally staging filter choices on mobile.
   * The staging decision lives with the facets owner so no global capture
   * listener has to suppress native change events.
   */
  #isMobileStagedSession() {
    return this.dataset.kvMobileStagedFilters === 'true' && !mediaQueryLarge.matches;
  }

  /**
   * Applies the current form state to the URL and re-renders the owning section.
   * @returns {Promise<void>}
   */
  #commitCurrentFilters() {
    this.#updateURLHash();
    const renderPromise = this.#updateSection();
    this.#dispatchPageUpdateEvent(renderPromise);
    return renderPromise;
  }

  /**
   * Updates filters. Mobile drawer input changes are staged until the explicit
   * VIEW RESULTS action commits the current form state.
   */
  updateFilters = () => {
    if (this.#isMobileStagedSession()) {
      this.dataset.kvFiltersDirty = 'true';
      return;
    }

    return this.#commitCurrentFilters();
  };

  /**
   * Commits a staged mobile drawer session without changing desktop behavior.
   * @returns {Promise<void> | undefined}
   */
  commitStagedFilters = () => {
    if (!this.#isMobileStagedSession()) return;
    delete this.dataset.kvFiltersDirty;
    return this.#commitCurrentFilters();
  };

  /**
   * Updates the section
   * @returns {Promise<void>}
   */
  #updateSection() {
    const collection = this.closest('collection-component.kervelan-collection');

    if (collection instanceof HTMLElement) {
      const renderVersion = ++this.#collectionRenderVersion;
      const results = collection.querySelector(COLLECTION_RESULTS_SELECTOR);

      collection.classList.remove('kv-filter-results-ready');
      collection.classList.add('kv-filter-updating');
      if (results instanceof HTMLElement) results.setAttribute('aria-busy', 'true');

      return sectionRenderer
        .renderSection(this.sectionId, {
          targetSelector: COLLECTION_RESULTS_SELECTOR,
          minimumCommitDelayMs: COLLECTION_FILTER_FADE_MS,
        })
        .then((html) => {
          if (renderVersion !== this.#collectionRenderVersion) return;

          const renderedCollection = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('collection-component.kervelan-collection');

          collection.classList.add('kv-filter-results-ready');
          this.#syncCollectionProductCount(renderedCollection);
          this.#syncCollectionFilterShell(renderedCollection);

          const resultsList = collection.querySelector('results-list');
          if (resultsList instanceof HTMLElement && typeof resultsList.refreshAfterFilterUpdate === 'function') {
            resultsList.refreshAfterFilterUpdate();
          }
        })
        .finally(() => {
          if (renderVersion !== this.#collectionRenderVersion) return;

          requestAnimationFrame(() => {
            if (renderVersion !== this.#collectionRenderVersion) return;
            collection.classList.remove('kv-filter-updating', 'kv-filter-results-ready');
            const currentResults = collection.querySelector(COLLECTION_RESULTS_SELECTOR);
            if (currentResults instanceof HTMLElement) currentResults.removeAttribute('aria-busy');
          });
        });
    }

    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      return startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      return sectionRenderer.renderSection(this.sectionId).then(() => {});
    }
  }

  #syncCollectionFormsFromURL() {
    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    const params = new URL(window.location.href).searchParams;

    for (const form of section.querySelectorAll('facets-form-component form.facets__form')) {
      if (!(form instanceof HTMLFormElement)) continue;

      for (const input of form.querySelectorAll('input[name^="filter."]')) {
        if (!(input instanceof HTMLInputElement)) continue;

        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = params.getAll(input.name).includes(input.value);
        } else {
          input.value = params.get(input.name) ?? '';
        }
      }
    }

    this.#syncCollectionSortingFromURL(params);

    const sortForm = section.querySelector('.kervelan-collection__sort-form');
    if (sortForm instanceof HTMLFormElement) {
      sortForm.querySelectorAll('input[type="hidden"][name^="filter."]').forEach((input) => input.remove());

      const sortSelect = sortForm.querySelector('select[name="sort_by"]');
      const sortBy = params.get('sort_by');
      if (sortSelect instanceof HTMLSelectElement && sortBy) sortSelect.value = sortBy;

      for (const [name, value] of params) {
        if (!name.startsWith('filter.')) continue;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        sortForm.appendChild(input);
      }
    }
  }

  #syncCollectionSortingFromURL(params) {
    const collection = this.closest('collection-component.kervelan-collection');
    if (!(collection instanceof HTMLElement)) return;

    const defaultSort = collection.querySelector('sorting-filter-component [data-default-sort-by]');
    const sortBy = params.get('sort_by') || (defaultSort instanceof HTMLElement ? defaultSort.dataset.defaultSortBy : '');
    if (!sortBy) return;

    for (const select of collection.querySelectorAll(
      'select[name="sort_by"], select.kv-mobile-collection-controls__sort-fallback'
    )) {
      if (!(select instanceof HTMLSelectElement)) continue;
      if (Array.from(select.options).some((option) => option.value === sortBy)) select.value = sortBy;
    }

    for (const input of collection.querySelectorAll('input[type="radio"][name="sort_by"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = input.value === sortBy;

      const option = input.closest('[role="option"]');
      if (option instanceof HTMLElement) {
        option.setAttribute('aria-selected', String(input.checked));
        option.tabIndex = input.checked ? 0 : -1;
      }
    }

    for (const component of collection.querySelectorAll('sorting-filter-component')) {
      const selected = component.querySelector('input[type="radio"][name="sort_by"]:checked');
      const status = component.querySelector('.facets__status');
      if (selected instanceof HTMLInputElement && status instanceof HTMLElement) {
        status.textContent = selected.dataset.optionName ?? '';
      }
    }
  }

  #getLiveResultsCount() {
    const section = this.closest('.shopify-section');
    const marker = section?.querySelector(COLLECTION_RESULTS_COUNT_SELECTOR);
    if (!(marker instanceof HTMLElement)) return;

    const count = Number(marker.dataset.kvResultsCount);
    return Number.isFinite(count) ? count : undefined;
  }

  #syncCollectionProductCount(renderedCollection) {
    const collection = this.closest('collection-component.kervelan-collection');
    if (!(collection instanceof HTMLElement)) return;

    const count = this.#getLiveResultsCount();
    if (count === undefined) return;

    const label = collection.querySelector('.kervelan-collection__count');
    if (label instanceof HTMLElement) {
      label.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
    }

    const renderedCount = renderedCollection?.querySelector('.products-count-wrapper [role="status"]');
    if (renderedCount instanceof HTMLElement) {
      for (const status of collection.querySelectorAll('.products-count-wrapper [role="status"]')) {
        status.textContent = renderedCount.textContent;
      }
    }

    for (const form of collection.querySelectorAll('facets-form-component')) {
      if (form instanceof HTMLElement) form.dataset.resultsCount = String(count);
    }
  }

  #getActiveCollectionFilterCount() {
    const params = new URL(window.location.href).searchParams;
    let count = 0;
    let hasPriceRange = false;

    for (const [key, value] of params) {
      if (!key.startsWith('filter.') || value.trim() === '') continue;

      if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
        hasPriceRange = true;
        continue;
      }

      count += 1;
    }

    return count + (hasPriceRange ? 1 : 0);
  }

  #syncCollectionFilterShell(renderedCollection) {
    const collection = this.closest('collection-component.kervelan-collection');
    if (!(collection instanceof HTMLElement)) return;

    if (renderedCollection instanceof HTMLElement) {
      for (const form of collection.querySelectorAll('facets-form-component form.facets__form')) {
        const renderedForm = renderedCollection.querySelector(`#${CSS.escape(form.id)}`);
        if (!(renderedForm instanceof HTMLFormElement)) continue;

        const pills = form.querySelector('.facets-remove');
        const renderedPills = renderedForm.querySelector('.facets-remove');
        if (pills && renderedPills) pills.replaceWith(renderedPills.cloneNode(true));

        for (const details of form.querySelectorAll('details.facets__panel[id]')) {
          const status = details.querySelector('facet-status-component');
          const renderedStatus = renderedForm
            .querySelector(`#${CSS.escape(details.id)}`)
            ?.querySelector('facet-status-component');
          if (status && renderedStatus) {
            status.replaceChildren(...Array.from(renderedStatus.cloneNode(true).childNodes));
          }
        }
      }

      const title = collection.querySelector('#filters-drawer-heading');
      const renderedTitle = renderedCollection.querySelector('#filters-drawer-heading');
      if (title && renderedTitle) {
        title.replaceChildren(...Array.from(renderedTitle.cloneNode(true).childNodes));
      }
    }

    const count = this.#getActiveCollectionFilterCount();
    const trigger = collection.querySelector('.kervelan-collection__filter-trigger');

    if (trigger instanceof HTMLElement) {
      let badge = trigger.querySelector('.kervelan-collection__filter-count');

      if (count === 0) {
        badge?.remove();
      } else {
        if (!(badge instanceof HTMLElement)) {
          badge = document.createElement('span');
          badge.className = 'kervelan-collection__filter-count';
          trigger.appendChild(badge);
        }

        badge.textContent = String(count);
      }
    }

    const mobileTrigger = collection.querySelector('.kv-mobile-collection-controls__filter');
    if (mobileTrigger instanceof HTMLButtonElement) {
      mobileTrigger.textContent = count > 0 ? `FILTER (${count})` : 'FILTER';
    }

    const drawerBadge = collection.querySelector('#filters-drawer-bubble');
    if (drawerBadge instanceof HTMLElement) {
      drawerBadge.hidden = count === 0;
      drawerBadge.textContent = String(count);
    }
  }

  #dispatchPageUpdateEvent(renderPromise) {
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  }

  /**
   * Dispatches search:update event if on the search page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchSearchUpdateEvent(renderPromise) {
    const query = this.#getSearchQuery();

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey(),
        },
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with result count
    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }

  /**
   * Gets the count of product results from the server-rendered data attribute.
   * @returns {number}
   */
  #getResultsCount() {
    return this.#getLiveResultsCount() ?? (Number(this.dataset.resultsCount) || 0);
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.delete('page');

    if (this.closest('collection-component.kervelan-collection')) {
      history.replaceState('', '', nextUrl.toString());
    } else {
      history.pushState('', '', nextUrl.toString());
    }
    return this.refreshFromCurrentURL();
  }

  refreshFromCurrentURL = () => {
    this.#syncCollectionFormsFromURL();

    const renderPromise = this.#updateSection();
    this.#dispatchPageUpdateEvent(renderPromise);
    return renderPromise;
  };

  /**
   * Dispatches collection:update event if on a collection page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchCollectionUpdateEvent(renderPromise) {
    // Build collection object with available identifiers
    // Only use numeric IDs — non-numeric values like "all" fail GID validation
    /** @type {any} */
    const rawId = this.dataset.collectionId;
    const collectionData = {
      id: rawId && /^\d+$/.test(rawId) ? rawId : null,
      handle: /** @type {string} */ (this.dataset.collectionHandle),
      productsCount: this.#getResultsCount(),
    };

    const deferredPromise = CollectionUpdateEvent.createPromise();
    const productFilters = CollectionUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: collectionData,
        ...(productFilters && { productFilters }),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with product count
    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

if (!window.__KervelanCollectionFilterHistoryBound) {
  window.__KervelanCollectionFilterHistoryBound = true;

  window.addEventListener('popstate', () => {
    const collection = document.querySelector('collection-component.kervelan-collection');
    const facetsForm = collection?.querySelector(
      'facets-form-component:not([data-kv-mobile-staged-filters="true"])'
    );

    if (facetsForm instanceof FacetsFormComponent) {
      facetsForm.refreshFromCurrentURL();
    }
  });
}

if (!window.KervelanCollectionHistory) {
  window.KervelanCollectionHistory = {
    applySort(value) {
      if (typeof value !== 'string' || value === '') return Promise.resolve();

      const collection = document.querySelector('collection-component.kervelan-collection');
      if (!(collection instanceof HTMLElement)) return Promise.resolve();

      const url = new URL(window.location.href);
      if (url.searchParams.get('sort_by') === value) return Promise.resolve();

      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');

      history.replaceState(
        { urlParameters: url.searchParams.toString() },
        '',
        url.toString()
      );

      const facetsForm =
        collection.querySelector('facets-form-component:not([data-kv-mobile-staged-filters="true"])') ||
        collection.querySelector('facets-form-component');

      if (facetsForm instanceof FacetsFormComponent) {
        return facetsForm.refreshFromCurrentURL();
      }

      window.location.replace(url.toString());
      return Promise.resolve();
    },
  };
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('pointerdown', this.#warmMoneyFormatting);
    this.addEventListener('focusin', this.#warmMoneyFormatting);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('pointerdown', this.#warmMoneyFormatting);
    this.removeEventListener('focusin', this.#warmMoneyFormatting);
  }

  #warmMoneyFormatting = () => {
    loadMoneyFormatting().catch(() => {
      // The actual change handler keeps a network-safe fallback if this optional warmup fails.
    });
  };

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results. Money parsing/formatting is loaded only after
   * real interaction with the price facet so catalog startup does not fetch it.
   */
  async updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    let moneyFormatting;
    try {
      moneyFormatting = await loadMoneyFormatting();
    } catch (error) {
      console.warn('[facets] Unable to load price formatting runtime:', error);
      // Preserve filtering even if this optional client-side formatting helper fails.
      facetsForm.updateFilters();
      return;
    }

    this.#adjustToValidValues(minInput, moneyFormatting);
    this.#adjustToValidValues(maxInput, moneyFormatting);

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary(moneyFormatting);
  }

  /**
   * Parses a formatted money value into minor units
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @param {{convertMoneyToMinorUnits: Function}} moneyFormatting - Lazy money-formatting module
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency, moneyFormatting) {
    return moneyFormatting.convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   * @param {{convertMoneyToMinorUnits: Function, formatMoney: Function}} moneyFormatting - Lazy money-formatting module
   */
  #adjustToValidValues(input, moneyFormatting) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    const value = this.#parseDisplayValue(input.value, currency, moneyFormatting);
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency, moneyFormatting);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency, moneyFormatting);

    if (value < min) {
      input.value = moneyFormatting.formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = moneyFormatting.formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   * @param {{convertMoneyToMinorUnits: Function, formatMoney: Function}} moneyFormatting - Lazy money-formatting module
   */
  #updateSummary(moneyFormatting) {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updatePriceSummary(minInput, maxInput, moneyFormatting);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey || event.ctrlKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle('facets__clear--active', filters.length > 0);
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle(activeClass, filters.length > 0);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   * @param {{convertMoneyToMinorUnits: Function, formatMoney: Function}} moneyFormatting - Lazy money-formatting module
   */
  updatePriceSummary(minInput, maxInput, moneyFormatting) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency, moneyFormatting);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency, moneyFormatting);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum, moneyFormatting)}–${this.#formatMoney(maxInputNum, moneyFormatting)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @param {{convertMoneyToMinorUnits: Function}} moneyFormatting - Lazy money-formatting module
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '', moneyFormatting) {
    const result = moneyFormatting.convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    const fallbackResult = moneyFormatting.convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @param {{formatMoney: Function}} moneyFormatting - Lazy money-formatting module
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue, moneyFormatting) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return moneyFormatting.formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
